import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { DiscardDialog } from "@/components/DiscardDialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  updateMemberAppointmentProfile,
  configureStaffServices,
  uploadStoreImage,
} from "@/lib/api/admin";
import type { StoreMember, AppointmentService } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/Textarea";
import { ImageUpload } from "@/components/ImageUpload";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/Spinner";
import { ScheduleInput } from "@/components/schedule/ScheduleInput";
import {
  scheduleToForm,
  formToSchedule,
  validateRawSchedule,
  type ScheduleFormState,
} from "@/lib/utils/schedule";

const schema = z.object({
  bio: z.string().max(700, "Bio must be 700 characters or fewer."),
  bookable: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

type MemberAppointmentProfileDialogProps = {
  open: boolean;
  onClose: () => void;
  storeId: string;
  member: StoreMember;
  services: AppointmentService[];
  initialServiceIds: string[];
};

export function MemberAppointmentProfileDialog({
  open,
  onClose,
  storeId,
  member,
  services,
  initialServiceIds,
}: MemberAppointmentProfileDialogProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [photoUrl, setPhotoUrl] = useState(member.photo_url);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [schedule, setSchedule] = useState<ScheduleFormState>(() =>
    scheduleToForm(member.weekly_schedule)
  );
  const [scheduleErrors, setScheduleErrors] = useState<Record<string, string>>({});
  const [initialSchedule, setInitialSchedule] = useState<ScheduleFormState>(() =>
    scheduleToForm(member.weekly_schedule)
  );
  const [useStoreHours, setUseStoreHours] = useState(
    () => !member.weekly_schedule || Object.keys(member.weekly_schedule).length === 0
  );
  const [initialUseStoreHours, setInitialUseStoreHours] = useState(useStoreHours);
  const [serviceIds, setServiceIds] = useState<string[]>(initialServiceIds);

  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      bio: member.bio ?? "",
      bookable: member.bookable,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        bio: member.bio ?? "",
        bookable: member.bookable,
      });
      setImageFile(null);
      setPhotoUrl(member.photo_url);
      const initial = scheduleToForm(member.weekly_schedule);
      setSchedule(initial);
      setInitialSchedule(initial);
      setScheduleErrors({});
      const initialUseStore =
        !member.weekly_schedule || Object.keys(member.weekly_schedule).length === 0;
      setUseStoreHours(initialUseStore);
      setInitialUseStoreHours(initialUseStore);
      setServiceIds(initialServiceIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member, reset]);

  function toggleService(id: string) {
    setServiceIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  }

  const scheduleDirty =
    JSON.stringify(schedule) !== JSON.stringify(initialSchedule) ||
    useStoreHours !== initialUseStoreHours;
  const servicesDirty =
    JSON.stringify([...serviceIds].sort()) !== JSON.stringify([...initialServiceIds].sort());

  function tryClose() {
    if (
      isDirty ||
      scheduleDirty ||
      servicesDirty ||
      imageFile !== null ||
      photoUrl !== member.photo_url
    ) {
      setDiscardOpen(true);
    } else {
      onClose();
    }
  }

  async function onSubmit(values: FormValues) {
    if (!useStoreHours) {
      const errs = validateRawSchedule(formToSchedule(schedule));
      if (errs) {
        setScheduleErrors(errs);
        return;
      }
    }
    setScheduleErrors({});

    setIsSubmitting(true);
    try {
      let finalPhotoUrl = photoUrl;
      if (imageFile) {
        const res = await uploadStoreImage(user!, storeId, imageFile, "appointment_staff");
        finalPhotoUrl = res.url;
      }

      await updateMemberAppointmentProfile(user!, storeId, member.user_uid, {
        bio: values.bio.trim(),
        photo_url: finalPhotoUrl ?? "",
        bookable: values.bookable,
        weekly_schedule: useStoreHours ? {} : formToSchedule(schedule),
      });

      await configureStaffServices(user!, storeId, member.user_uid, serviceIds);

      await qc.invalidateQueries({ queryKey: ["store-members", storeId] });
      toast.success("Appointment profile updated.");
      onClose();
    } catch (e) {
      setError("root", { message: (e as Error).message });
    } finally {
      setIsSubmitting(false);
    }
  }

  const memberName = `${member.first_name} ${member.last_name}`.trim() || member.user_uid;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) tryClose();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Appointment Profile — {memberName}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5 py-2">
            <Textarea
              id="map-bio"
              label="Bio (optional)"
              placeholder="e.g. Senior colorist with 10 years of experience"
              rows={3}
              maxLength={700}
              aria-invalid={!!errors.bio}
              error={errors.bio?.message}
              {...register("bio")}
            />

            <ImageUpload
              id="map-photo"
              label="Photo"
              aspectRatio="1:1"
              enableCrop
              currentUrl={photoUrl}
              onFileSelect={setImageFile}
              onClear={() => {
                setImageFile(null);
                setPhotoUrl(undefined);
              }}
              className="w-44"
            />

            <div className="flex items-center justify-between gap-3 rounded-md border p-3">
              <div>
                <Label>Bookable</Label>
                <p className="text-xs text-muted-foreground">
                  Shown to customers as a staff member they can book an appointment with.
                </p>
              </div>
              <Controller
                control={control}
                name="bookable"
                render={({ field }) => (
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label>Weekly Schedule</Label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={useStoreHours}
                  onCheckedChange={(checked) => setUseStoreHours(!!checked)}
                  aria-label="Use store hours"
                />
                <span className="text-sm">Use store hours</span>
              </label>
              {!useStoreHours && (
                <ScheduleInput
                  value={schedule}
                  onChange={(v) => {
                    setSchedule(v);
                    setScheduleErrors({});
                  }}
                  errors={scheduleErrors}
                />
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Services offered</Label>
              {services.length === 0 ? (
                <p className="text-xs text-muted-foreground py-1">
                  Create a service in the Services tab first, then assign it here.
                </p>
              ) : (
                <div className="border rounded-md divide-y">
                  {services.map((service) => (
                    <label
                      key={service.id}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={serviceIds.includes(service.id)}
                        onCheckedChange={() => toggleService(service.id)}
                        disabled={isSubmitting}
                      />
                      <span className="flex-1 min-w-0 text-sm truncate">{service.name}</span>
                      {!service.active && (
                        <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full shrink-0">
                          Inactive
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {errors.root?.message && (
              <p className="text-sm text-destructive">{errors.root.message}</p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={tryClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Spinner className="size-4 border-background" /> : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}
